import { OrbitControls, OrthographicCamera, PerspectiveCamera } from '@react-three/drei';
import { useThree } from '@react-three/fiber';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import { setRefresh } from '@store/reducers/viewerSlice';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Box3, Vector3, type Object3D, type Object3DEventMap, type OrthographicCamera as ThreeOrthographicCamera, type PerspectiveCamera as ThreePerspectiveCamera } from 'three';
import { type OrbitControls as ThreeOrbitControls } from 'three-stdlib';

interface CameraControllerProps {
  model: Object3D<Object3DEventMap>;
}

const CameraController: React.FC<CameraControllerProps> = ({ model }) => {
  const { camera, size } = useThree((state) => state);
  const orbitControlsRef = useRef<ThreeOrbitControls>(null);
  const perspectiveCameraRef = useRef<ThreePerspectiveCamera>(null);
  const orthographicCameraRef = useRef<ThreeOrthographicCamera>(null);
  const { isPerspective, isRefreshed, isMeasuring } = useAppSelector(state => state.viewer);
  const dispatch = useAppDispatch();
  const [isReady, setReady] = useState<boolean>(false);
  const [isInitialized, setInitialized] = useState<boolean>(false);

  const fov = 50;
  const pixelsFromCenterToTop = size.height / 2;
  const fovFactor = Math.tan(((fov / 2) * Math.PI) / 180) / pixelsFromCenterToTop;

  const persDistanceToOrthoZoom = useCallback((distance: number) => 1 / fovFactor / distance, [fovFactor]);

  const orthoZoomToPersDistance = useCallback((zoom: number) => 1 / zoom / fovFactor, [fovFactor]);

  // Calculate minimum zoom based on maxDistance
  const calculateMaxDistance = useCallback((model: Object3D) => {
    const bbox = new Box3().setFromObject(model);
    const size = bbox.getSize(new Vector3());
    const maxSize = Math.max(size.x, size.y, size.z);
    return maxSize * 2.5; // Adjust multiplier as needed
  }, []);

  const maxDistance = calculateMaxDistance(model);
  const minZoom = persDistanceToOrthoZoom(maxDistance);

  const setObjectToCenter = useCallback(() => {
    const bbox = new Box3().setFromObject(model);
    const objectCenter = bbox.getCenter(new Vector3());
    const size = bbox.getSize(new Vector3());
    const distance = Math.max(size.x, size.y, size.z);

    camera.position.set(objectCenter.x - distance, objectCenter.y + distance, objectCenter.z + distance);
    camera.lookAt(objectCenter);
    camera.updateProjectionMatrix();

    if (orbitControlsRef.current) {
      orbitControlsRef.current.target.copy(objectCenter);
      orbitControlsRef.current.update();
    }

    // Ensure initial zoom respects minimum zoom
    if (orthographicCameraRef.current) {
      const newZoom = persDistanceToOrthoZoom(orthographicCameraRef.current.position.length());
      orthographicCameraRef.current.zoom = Math.max(newZoom, minZoom);
      orthographicCameraRef.current.updateProjectionMatrix();
    }
  }, [camera, minZoom, model, persDistanceToOrthoZoom]);

  useEffect(() => {
    if (perspectiveCameraRef.current && orthographicCameraRef.current) {
      if (isPerspective) {
        perspectiveCameraRef.current.position.copy(orthographicCameraRef.current.position.clone());
        const distance = orthoZoomToPersDistance(orthographicCameraRef.current.zoom);
        perspectiveCameraRef.current.position.setLength(distance);
      } else {
        orthographicCameraRef.current.position.copy(perspectiveCameraRef.current.position.clone());
        const newZoom = persDistanceToOrthoZoom(orthographicCameraRef.current.position.length());
        // Ensure zoom doesn't go below minimum
        orthographicCameraRef.current.zoom = Math.max(newZoom, minZoom);
        orthographicCameraRef.current.updateProjectionMatrix();
      }
    }

    if (orbitControlsRef.current) {
      const bbox = new Box3().setFromObject(model);
      const objectCenter = bbox.getCenter(new Vector3());

      orbitControlsRef.current.target = new Vector3(objectCenter.x, objectCenter.y, objectCenter.z);
      orbitControlsRef.current.update();
    }

    setReady(true);
  }, [camera, isPerspective, minZoom, model, orthoZoomToPersDistance, persDistanceToOrthoZoom]);

  useEffect(() => {
    if (orbitControlsRef.current) {
      orbitControlsRef.current.enableRotate = !isMeasuring;
      orbitControlsRef.current.maxDistance = maxDistance;
    }
  }, [isMeasuring, maxDistance]);

  useEffect(() => {
    if (isRefreshed === true) {
      setObjectToCenter();
      dispatch(setRefresh(false));
    }
  }, [dispatch, isRefreshed, setObjectToCenter]);

  useEffect(() => {
    if (isReady && !isInitialized) {
      setObjectToCenter();
      setInitialized(true);
    }
  }, [isInitialized, isReady, setObjectToCenter]);

  useEffect(() => {
    const handleTouchMove = (event: TouchEvent) => event.preventDefault();

    const canvas = document.querySelector('#threejs-canvas') as HTMLCanvasElement;
    canvas.addEventListener('touchmove', handleTouchMove, { passive: false });

    return () => {
      canvas.removeEventListener('touchmove', handleTouchMove);
    };
  }, []);

  return <>
    <OrbitControls
      makeDefault
      ref={orbitControlsRef}
      enableDamping={true}
      dampingFactor={0.25}
      rotateSpeed={0.5}
      maxDistance={maxDistance}
      minZoom={minZoom}
    />
    <PerspectiveCamera ref={perspectiveCameraRef} makeDefault={isPerspective} fov={fov} />
    <OrthographicCamera ref={orthographicCameraRef} makeDefault={!isPerspective} />
  </>;
};

export default CameraController;
